// components/intelligence/DataSnapshotTimeline.jsx
import React, { useState, useEffect } from 'react';
import { Check, X, Clock, Database } from 'lucide-react';

/**
 * Vertical, minimal snapshot timeline with summary
 */
export function DataSnapshotTimeline({ onSnapshotSelect, currentDate, selectedTab }) {
    const [snapshots, setSnapshots] = useState([]);
    const [loading, setLoading] = useState(true);

    // Define which sources are relevant for each tab
    const tabSources = {
        overview: ['market-statistics', 'genre-analytics', 'pricing-analysis', 'publisher-analytics'],
        trends: ['temporal-trends', 'trending-games', 'regional-distribution'],
        opportunities: ['acquisition-targets', 'indie-gems', 'competitive-analysis'],
        competitive: ['competitive-analysis', 'publisher-analytics']
    };

    useEffect(() => {
        fetchSnapshots();
    }, []);

    const fetchSnapshots = async () => {
        try {
            const response = await fetch('/analytics/v2/market-intelligence/snapshots');
            const data = await response.json();

            if (data.snapshots) {
                setSnapshots(data.snapshots);
                // If no current date is selected by parent, select the most recent snapshot with data
                if (!currentDate && data.snapshots.length > 0) {
                    const snapshotWithData = data.snapshots.find((s) => s.hasData);
                    if (snapshotWithData && onSnapshotSelect) {
                        onSnapshotSelect(snapshotWithData);
                    }
                }
            }
        } catch (error) {
            console.error('[Timeline] Error fetching snapshots:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSnapshotSelect = (snapshot) => {
        if (onSnapshotSelect) {
            onSnapshotSelect(snapshot);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getSourceLabel = (sourceName) => {
        const labels = {
            'market-statistics': 'Market Stats',
            'genre-analytics': 'Genre Analytics',
            'pricing-analysis': 'Pricing',
            'temporal-trends': 'Temporal',
            'trending-games': 'Trending',
            'regional-distribution': 'Regional',
            'acquisition-targets': 'Acquisition',
            'indie-gems': 'Indie Gems',
            'competitive-analysis': 'Competitive',
            'publisher-analytics': 'Publisher'
        };
        return labels[sourceName] || sourceName;
    };

    if (loading) {
        return (
            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
                <div className="flex items-center justify-center text-sm text-gray-600">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                    Loading snapshots...
                </div>
            </div>
        );
    }

    if (snapshots.length === 0) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 text-sm text-gray-700 flex items-center space-x-2">
                <Clock className="h-4 w-4 text-gray-500" />
                <span>No snapshots available</span>
            </div>
        );
    }

    // Get relevant sources for current tab
    const relevantSources = tabSources[selectedTab] || [];
    const allSources = Object.keys(snapshots.find((s) => s.date === currentDate)?.sources || {});
    const sourcesToShow = relevantSources.length > 0 ? relevantSources : allSources;

    const selectedSnapshot = snapshots.find((s) => s.date === currentDate) || snapshots[0];
    const hasRelevantData =
        selectedSnapshot && sourcesToShow.some((source) => selectedSnapshot.sources[source]?.hasData);
    const relevantSourceCount = selectedSnapshot
        ? sourcesToShow.filter((source) => selectedSnapshot.sources[source]?.hasData).length
        : 0;

    return (
        <div className="h-full flex flex-col">
            {/* Summary */}
            <div className="mb-3 space-y-1 flex-shrink-0">
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                    <Database className="h-4 w-4 text-blue-600" />
                    <span className="font-semibold">Snapshot</span>
                </div>
                <div className="text-sm text-gray-900 font-semibold">
                    {selectedSnapshot ? formatDate(selectedSnapshot.date) : 'No selection'}
                </div>
                <div className="text-xs text-gray-500">
                    {selectedSnapshot ? formatTime(selectedSnapshot.timestamp) : '—'}
                </div>
                <div className="text-xs text-gray-700">
                    Sources: {sourcesToShow.map((s) => getSourceLabel(s)).join(', ') || '—'}
                </div>
                <div className="flex items-center space-x-2 text-xs">
                    <span
                        className={`px-2 py-1 rounded-full ${
                            hasRelevantData ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}
                    >
                        Data {hasRelevantData ? 'available' : 'missing'}: {relevantSourceCount}/
                        {sourcesToShow.length || 0}
                    </span>
                </div>
            </div>

            {/* Vertical list */}
            <div className="flex-1 space-y-3 overflow-y-auto pr-1 min-h-0">
                {snapshots.map((snapshot, index) => {
                    const isSelected = currentDate === snapshot.date || (!currentDate && index === 0);
                    const hasData = snapshot.hasData;
                    return (
                        <button
                            key={snapshot.date}
                            onClick={() => handleSnapshotSelect(snapshot)}
                            className={`w-full text-left border rounded-lg p-3 transition-colors ${
                                isSelected
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                            }`}
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-sm font-semibold text-gray-900">
                                        {formatDate(snapshot.date)}
                                    </div>
                                    <div className="text-xs text-gray-500">{formatTime(snapshot.timestamp)}</div>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <span
                                        className={`h-2.5 w-2.5 rounded-full ${
                                            hasData ? 'bg-green-500' : 'bg-gray-400'
                                        }`}
                                    />
                                    {index === 0 && (
                                        <span className="text-[11px] text-orange-600 font-semibold">Latest</span>
                                    )}
                                </div>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-600">
                                {sourcesToShow
                                    .filter((name) => snapshot.sources[name])
                                    .map((name) => {
                                        const info = snapshot.sources[name];
                                        return (
                                            <span
                                                key={name}
                                                className={`px-2 py-1 rounded-full border ${
                                                    info.hasData
                                                        ? 'border-green-200 text-green-700'
                                                        : 'border-gray-200 text-gray-500'
                                                }`}
                                            >
                                                {getSourceLabel(name)}
                                            </span>
                                        );
                                    })}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default DataSnapshotTimeline;
